import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, InputAdornment, TextField, Tooltip } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTriangleExclamation,
  faVials,
} from "@fortawesome/pro-regular-svg-icons";
import { faUserMagnifyingGlass } from "@fortawesome/pro-solid-svg-icons";
import { useRecoilState, useRecoilValue } from "recoil";
import debounce from "debounce";
import { EMPTY_CONTEXT } from "@bto/shared";
import {
  GlobalSearchModal,
  SESSION_KEY_STATE,
} from "../GlobalSearch/GlobalSearchModal";
import {
  affiliateRequisition,
  attestGlobalSearch,
  getAttestationState,
  getProviderAccounts,
  searchGlobalPatients,
} from "../GlobalSearch/Services/GlobalSearchService";
import MyGrid from "./Grid";
import {
  CustomGridTab,
  CustomGridWrapper,
  GlobalSearchButtonArea,
  GlobalSearchIconButton,
  HeaderActionItem,
  HeaderActionNeededItem,
  HeaderActionsArea,
  HeaderLayout,
  HeaderSearchArea,
  HeaderTabsArea,
  HeaderTabsContainer,
  HeaderTabsScroller,
  MissingInfoWarning,
  MissingInfoWarningText,
  ProviderHeaderActionButton,
  ProviderHeaderRedrawButton,
  SearchInputBox,
  SubCustomGridTab,
  SubGridHeaderTabButtonWrapper,
  SubGridHeaderTabs,
  SubTabCount,
  SubTabNumber,
  TabPanelContent,
} from "./Grid.styles";
import { useLoginScreen } from "../../utils/hooks/useLoginScreen";
import { getTotalRequisitions } from "../../recoil/selectors";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import {
  currentEditDraftRequisition,
  currentNewRequisition,
  defaultReqsRequestComplete,
  defaultRequisitions,
  gridSearchText,
  missingRequisitions,
  orderedReqsRequestComplete,
  orderedRequisitions,
  searchRequisitions,
} from "../../recoil/atoms";
import {
  RefreshMissingInfo,
  RefreshNewRequisitionOrders,
  GetSpecificRequisition,
  RefreshRequisitionRows,
  GetArchivedRequisitions,
} from "./Services/GridService";
import { useToast, ActionSnackbar } from "./useToast";
import { useRequisitionRefresh } from "./useRequisitionRefresh";
import { useArchiveActions } from "./useArchiveActions";
import { useCfcAlerts } from "./useCfcAlerts";
import {
  convertRequisitionToRequisitionRow,
  getInitialNewRequisitionForm,
  searchCharacterCountValid,
} from "../../recoil/helpers";
import { MissingInfoModal } from "./MissingInfoModal";
import { PatientReleaseModal } from "./PatientReleaseModal";
import { RedrawModal } from "../Redraw/RedrawModal";
import {
  ReleasePatientData,
  AddOnModalData,
  SubTabName,
  TabName,
} from "./types";
import {
  Auth0UserSerializer,
  BetaFeature,
  DetailedRequisitionSerializer,
  RequisitionModelSerializer,
  RequisitionRow,
  TotalsCount,
} from "../../recoil/types";
import { SendActionType } from "../shared/Services/SharedServices";
import NewKitPickup from "../KitPickup/Index";
import NewRequisition from "../NewRequisition/Index";
import { NewReqEditOrderDialog } from "../NewRequisition/NewReqEditOrderDialog";
import { RequestReportUpdateDialog } from "../NewRequisition/RequestReportUpdateDialog";

import { ClearSearchIcon } from "../../assets/iconComponents/ClearSearchIcon";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";
import { defaultSort } from "../../utils/helpers";
import { convertNewReqTypeToForm } from "../NewRequisition/Services/helpers";
import { DRAFT_STATUS, NewRequisitionType } from "../NewRequisition/types";
import { ProviderLetterModal } from "../UserReport/ProviderLetterModal";
import { AddOnModal } from "./AddOnModal";
import { AlertsButton } from "../Cfc/AlertsButton";
import { AlertTab, CfcEligibilityModal } from "../Cfc/CfcEligibilityModal";
import { CfcOrderModal } from "../Cfc/CfcOrderModal";
import { Banner } from "../Banner/Banner";
import {
  getShouldShowBetaBanner,
  handleBetaFeatureResponse,
  useBetaFeatures,
} from "../Banner/Services/BetaBannerService";
import { TrackAnalyticsEvent } from "../../services/Analytics";
import {
  useBooleanFeatureFlag,
  useVariantFeatureFlag,
} from "../../utils/hooks/useFeatureFlag";
import { FeatureFlags } from "../../utils/launchDarklyUtils";
import { useRedrawAlertDismissal } from "./useRedrawAlertDismissal";

interface TabPanelProps {
  children: JSX.Element;
  index: number;
  value: number;
}

const maxReqsToLoad = 500;
const PROVIDER_ADVOCACY_DISMISSED_KEY = "providerAdvocacyBannerDismissed";

const getTotalReqs = (activeTab: number, totalReqs?: TotalsCount) => {
  if (totalReqs) {
    if (activeTab === 0 && totalReqs.totalCompletedCount > maxReqsToLoad) {
      return totalReqs.totalCompletedCount;
    }
    if (activeTab === 1 && totalReqs.totalProcessingCount > maxReqsToLoad) {
      return totalReqs.totalProcessingCount;
    }
  }
};

const TabPanel: FC<TabPanelProps> = (props) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      data-testid={`tabpanel-${index + 1}`}
      {...other}
    >
      {value === index && <TabPanelContent>{children}</TabPanelContent>}
    </div>
  );
};

export const getTabIndex = (
  tabName?: TabName | null,
  tabs?: TabName[],
): number => {
  if (!tabName || !tabs) return 0;
  const index = tabs.indexOf(tabName);
  return index >= 0 ? index : 0;
};

export const CustomGrid = (): React.ReactElement => {
  useLoginScreen();
  const { deviceType } = useDeviceHook();
  const { auth, setLoggedOut } = useAuthenticationHook();
  const tabs: TabName[] = useMemo(
    () =>
      auth?.is_electronic_ordering_enabled
        ? ["COMPLETED", "PROCESSING", "ORDERED", "ARCHIVED"]
        : ["COMPLETED", "PROCESSING", "ARCHIVED"],
    [auth?.is_electronic_ordering_enabled],
  );
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage?.getItem("btoProviderCurrentTab") as TabName;
    return getTabIndex(savedTab, tabs);
  });
  // Track which tab user was on when opening NewRequisition (for return navigation)
  const originatingTabRef = useRef(activeTab);
  // Re-sync activeTab when tabs array changes (e.g., after auth loads)
  useEffect(() => {
    const savedTab = localStorage?.getItem("btoProviderCurrentTab") as TabName;
    if (savedTab && tabs.includes(savedTab)) {
      setActiveTab(tabs.indexOf(savedTab));
    }
  }, [tabs.length]);
  const [activeSubTab, setActiveSubTab] = useState<SubTabName>("ORDERED");
  const [missingInfoModalOpen, setMissingInfoModalOpen] = useState(false);
  const [patientReleaseModalOpen, setPatientReleaseModalOpen] = useState<
    ReleasePatientData | undefined
  >(undefined);
  const [addOnModalData, setAddOnModalData] = useState<
    AddOnModalData | undefined
  >(undefined);
  const subTabs: SubTabName[] = ["ORDERED", "DRAFTS"];

  const searchRef = useRef<HTMLInputElement>();
  const totalReqs = useRecoilValue(getTotalRequisitions);

  const [globalSearchText, setGlobalSearchText] =
    useRecoilState(gridSearchText);
  const [searchText, setSearchText] = useState(globalSearchText ?? "");
  const [allDefaultRequisitions, setRequisitions] =
    useRecoilState(defaultRequisitions);
  const [providerLetterOpen, setProviderLetterOpen] = useState<boolean>(false);
  const providerAdvocacyDismissedStorageKey = `${PROVIDER_ADVOCACY_DISMISSED_KEY}:${
    auth?.username ?? "unknown"
  }`;
  const [shouldShowProviderLetterBanner, setShouldShowProviderLetterBanner] =
    useState<boolean | undefined>(
      auth?.show_client_warning &&
        localStorage.getItem(providerAdvocacyDismissedStorageKey) !== "true",
    );
  const [searchGridRequisitions, setSearchRequisitions] =
    useRecoilState(searchRequisitions);
  const [allMissingRequisitions, setAllMissingRequisitions] =
    useRecoilState(missingRequisitions);
  const [missingRequestComplete, setMissingRequestComplete] = useState(false);
  const [allOrderedRequisitions, setAllOrderedRequisitions] =
    useRecoilState(orderedRequisitions);
  const [orderedRequestComplete, setOrderedRequestComplete] = useRecoilState(
    orderedReqsRequestComplete,
  );
  const [draftEditReq, setDraftEditReq] = useRecoilState(
    currentEditDraftRequisition,
  );
  const activeTotalReqs = getTotalReqs(activeTab, totalReqs);
  const [newGlobalRequisition, setGlobalNewRequisition] = useRecoilState(
    currentNewRequisition,
  );
  const [defaultRequestComplete] = useRecoilState(defaultReqsRequestComplete);
  const [draftModal, setDraftModal] = useState<
    "saved" | "deleted" | "delete" | "unsaved"
  >();
  const [newReqOpen, setNewReqOpen] = useState(false);
  const [newKitPickupOpen, setNewKitPickupOpen] = useState(false);
  const { enabled: globalSearchEnabled } = useBooleanFeatureFlag(
    FeatureFlags.ProviderGlobalSearch,
  );
  const [attestationModalOpen, setAttestationModalOpen] = useState(
    () => sessionStorage.getItem(SESSION_KEY_STATE) !== null,
  );
  const [redrawModalOpen, setRedrawModalOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState<
    NewRequisitionType | undefined
  >();
  const [reportUpdateRequisition, setReportUpdateRequisition] = useState<
    DetailedRequisitionSerializer | undefined
  >();
  const lastReportUpdateClickRef = useRef<string>();
  const [editingRedrawOrderId, setEditingRedrawOrderId] = useState<
    string | null
  >(null);
  const [betaFeature, setBetaFeature] = useState<BetaFeature | undefined>(
    undefined,
  );
  const [shouldShowBetaBanner, setShouldShowBetaBanner] =
    useState<boolean>(false);
  const [archivedRequisitions, setArchivedRequisitions] = useState<
    RequisitionRow[]
  >([]);
  const [archivedRequestComplete, setArchivedRequestComplete] = useState(false);
  const toast = useToast();

  const { variant: betaFeatureName } = useVariantFeatureFlag(
    FeatureFlags.BetaFeature,
    EMPTY_CONTEXT,
    auth?.username,
  );
  const { enabled: providerCfcEnabled } = useBooleanFeatureFlag(
    FeatureFlags.ProviderCfc,
  );
  const { enabled: editOrderEnabled } = useBooleanFeatureFlag(
    FeatureFlags.ProviderElectronicOrderEdit,
  );
  const { enabled: providerAdvocacyEnabled } = useBooleanFeatureFlag(
    FeatureFlags.ProviderAdvocacy,
  );
  const isSalesRep = auth?.user_type === "sales_rep";
  const canCreateElectronicOrders =
    !isSalesRep && Boolean(auth?.is_electronic_ordering_enabled);
  const canAccessProviderCfc =
    providerCfcEnabled && (canCreateElectronicOrders || isSalesRep);

  const refresh = useRequisitionRefresh({
    token: auth?.token,
    setLoggedOut,
    canUseProviderCfc: canAccessProviderCfc,
    searchText,
    setRequisitions,
    setSearchRequisitions,
    setArchivedRequisitions,
    setAllOrderedRequisitions,
    setOrderedRequestComplete,
  });

  const completedRows = useMemo(
    () =>
      allDefaultRequisitions?.requisitions_completed?.map(
        (requisition, index) =>
          convertRequisitionToRequisitionRow(
            requisition,
            index,
            canAccessProviderCfc,
          ),
      ) || [],
    [allDefaultRequisitions?.requisitions_completed, canAccessProviderCfc],
  );
  const processingRows = useMemo(
    () =>
      allDefaultRequisitions?.requisitions_processing?.map(
        (requisition, index) =>
          convertRequisitionToRequisitionRow(
            requisition,
            index,
            canAccessProviderCfc,
          ),
      ) || [],
    [allDefaultRequisitions?.requisitions_processing, canAccessProviderCfc],
  );
  const searchCompletedRows = useMemo(
    () =>
      searchGridRequisitions?.requisitions_completed?.map(
        (requisition, index) =>
          convertRequisitionToRequisitionRow(
            requisition,
            index,
            canAccessProviderCfc,
          ),
      ) || [],
    [canAccessProviderCfc, searchGridRequisitions?.requisitions_completed],
  );
  const searchProcessingRows = useMemo(
    () =>
      searchGridRequisitions?.requisitions_processing?.map(
        (requisition, index) =>
          convertRequisitionToRequisitionRow(
            requisition,
            index,
            canAccessProviderCfc,
          ),
      ) || [],
    [canAccessProviderCfc, searchGridRequisitions?.requisitions_processing],
  );

  const betaFeatures = useBetaFeatures(auth?.token, setLoggedOut);

  // Capture current tab whenever NewRequisition opens (covers all entry points)
  useEffect(() => {
    if (newReqOpen) {
      originatingTabRef.current = activeTab;
    }
  }, [newReqOpen, activeTab]);

  // Clear editingRedrawOrderId when modal closes via any path (SAVE DRAFT, DELETE, etc.)
  useEffect(() => {
    if (!newReqOpen) {
      setEditingRedrawOrderId(null);
    }
  }, [newReqOpen]);

  useEffect(() => {
    const setTabSelect = async () => {
      if (auth) {
        const currentTabName = tabs[activeTab];
        if (currentTabName === "COMPLETED") {
          await SendActionType(
            "ProviderLoadCompletedReqs",
            setLoggedOut,
            auth?.token,
          );
        } else if (currentTabName === "PROCESSING") {
          await SendActionType(
            "ProviderLoadProcessingReqs",
            setLoggedOut,
            auth?.token,
          );
        } else if (currentTabName === "ORDERED") {
          await SendActionType(
            "ProviderLoadOrderedReqs",
            setLoggedOut,
            auth?.token,
          );
        } else if (currentTabName === "ARCHIVED") {
          await SendActionType(
            "ProviderLoadArchivedReqs",
            setLoggedOut,
            auth?.token,
          );
        }
      }
    };
    setTabSelect();
  }, [activeTab, tabs]);

  const launchSearch = async (searchTextString: string) => {
    await RefreshRequisitionRows(
      auth!.token,
      setSearchRequisitions,
      setLoggedOut,
      searchTextString,
    );
  };

  const delayedSearch = useCallback(
    debounce(async (searchTextString: string) => {
      await launchSearch(searchTextString);
    }, 400),
    [],
  );

  const resetReqs = async () => {
    await RefreshRequisitionRows(auth!.token, setRequisitions, setLoggedOut);
  };

  const delayedResetReqs = useCallback(
    debounce(async () => {
      await resetReqs();
    }, 400),
    [],
  );

  useEffect(() => {
    const getSearchResults = async () => {
      if (auth) {
        setGlobalSearchText(searchText);
        if (searchCharacterCountValid(searchText)) {
          delayedSearch(searchText!);
        } else if (searchText.length > 1) {
          delayedResetReqs();
        }

        if ((!searchText || searchText.length < 3) && searchGridRequisitions) {
          setSearchRequisitions(undefined);
        }
      }
    };
    getSearchResults();
  }, [searchText]);

  useEffect(() => {
    const getMissingInfo = async () => {
      if (auth) {
        setMissingRequestComplete(false);
        try {
          await RefreshMissingInfo(
            auth.token,
            setAllMissingRequisitions,
            setLoggedOut,
          );
        } finally {
          setMissingRequestComplete(true);
        }
      }
    };
    getMissingInfo();
  }, [auth?.token]);

  // Fetch electronic orders on mount (for the Alerts modal Redraws tab) and when visiting ORDERED tab
  useEffect(() => {
    const getOrderedRequisitions = async () => {
      if (auth) {
        await RefreshNewRequisitionOrders(
          auth.token,
          setAllOrderedRequisitions,
          setLoggedOut,
          setOrderedRequestComplete,
        );
      }
    };
    getOrderedRequisitions();
  }, [auth?.token]);

  // Refresh electronic orders when visiting ORDERED tab
  useEffect(() => {
    const refreshOrderedRequisitions = async () => {
      if (auth && getTabIndex("ORDERED", tabs) === activeTab) {
        await RefreshNewRequisitionOrders(
          auth.token,
          setAllOrderedRequisitions,
          setLoggedOut,
          setOrderedRequestComplete,
        );
      }
    };
    refreshOrderedRequisitions();
  }, [activeTab]);

  useEffect(() => {
    const getArchivedRequisitions = async () => {
      if (auth && getTabIndex("ARCHIVED", tabs) === activeTab) {
        try {
          const response = await GetArchivedRequisitions(
            auth.token,
            setLoggedOut,
          );
          if (response.ok) {
            const data = await response.json();
            // Backend returns { requisitions_archived: [...], ... }
            // Transform raw data to RequisitionRow format
            const transformedData = (data.requisitions_archived || []).map(
              (req: RequisitionModelSerializer, index: number) =>
                convertRequisitionToRequisitionRow(
                  req,
                  index,
                  canAccessProviderCfc,
                ),
            );
            setArchivedRequisitions(transformedData);
          } else {
            toast.showError("Failed to load archived orders");
          }
        } catch {
          toast.showError("Failed to load archived orders");
        }
        setArchivedRequestComplete(true);
      }
    };
    getArchivedRequisitions();
  }, [activeTab, auth?.token, canAccessProviderCfc, tabs]);

  useEffect(() => {
    const checkShouldShowBetaBanner = async () => {
      // Wait until we're authenticated and have the feature list from the backend and chosen feature from flagging service
      if (auth && betaFeatures && betaFeatureName) {
        const foundBetaFeature = betaFeatures.find(
          (feature: BetaFeature) => feature.name === betaFeatureName,
        );

        let showBanner = false;
        if (foundBetaFeature) {
          // Set the feature for display if the name from flagging service matches a real feature
          setBetaFeature(foundBetaFeature);
          // Check with the backend if the user already responded
          showBanner = await getShouldShowBetaBanner(
            auth.token,
            foundBetaFeature.name,
            setLoggedOut,
          );
        }
        setShouldShowBetaBanner(showBanner);

        // If we show the banner, track that the user saw it
        if (showBanner) {
          TrackAnalyticsEvent("BetaFeatureBannerView", auth.token, undefined, {
            feature_name: foundBetaFeature!.name,
          });
        }
      }
    };
    checkShouldShowBetaBanner();
  }, [auth, betaFeatures, betaFeatureName]);

  const filteredCompletedRows = useMemo(() => {
    if (searchCharacterCountValid(searchText) && searchCompletedRows.length) {
      return searchCompletedRows;
    }
    return completedRows
      .filter(
        (row) =>
          row.name.toLowerCase().includes(searchText.toLowerCase()) ||
          row.physician.toLowerCase().includes(searchText.toLowerCase()) ||
          row.reqID.toLowerCase().includes(searchText.toLowerCase()) ||
          row.medicalRecordNumber
            .toLowerCase()
            .includes(searchText.toLowerCase()) ||
          row.reported.toLowerCase().includes(searchText.toLowerCase()),
      )
      .sort((valueA, valueB) =>
        defaultSort(
          new Date(valueB.reported).getTime(),
          new Date(valueA.reported).getTime(),
        ),
      );
  }, [completedRows, searchText, searchCompletedRows]);

  const filteredProcessingRows = useMemo(() => {
    if (searchCharacterCountValid(searchText) && searchProcessingRows.length) {
      return searchProcessingRows;
    }
    return processingRows
      .filter(
        (row) =>
          row.name.toLowerCase().includes(searchText.toLowerCase()) ||
          row.physician.toLowerCase().includes(searchText.toLowerCase()) ||
          row.reqID.toLowerCase().includes(searchText.toLowerCase()) ||
          row.medicalRecordNumber
            .toLowerCase()
            .includes(searchText.toLowerCase()) ||
          row.collected.toLowerCase().includes(searchText.toLowerCase()),
      )
      .sort((valueA, valueB) =>
        defaultSort(
          new Date(valueB.collected).getTime(),
          new Date(valueA.collected).getTime(),
        ),
      );
  }, [processingRows, searchText, searchProcessingRows]);

  const filteredOrderedRows = useMemo(
    () =>
      allOrderedRequisitions
        .filter(
          (row) =>
            row.ordering_health_care_providers
              .toLowerCase()
              .includes(searchText.toLowerCase()) ||
            `${row.first_name} ${row.last_name}`
              .toLowerCase()
              .includes(searchText.toLowerCase()) ||
            (row.medical_record_number || "")
              .toLowerCase()
              .includes(searchText.toLowerCase()) ||
            row.date_of_birth
              .toLowerCase()
              .includes(searchText.toLowerCase()) ||
            row.unity_complete_id
              .toLowerCase()
              .includes(searchText.toLowerCase()),
        )
        .filter((row) =>
          activeSubTab === "ORDERED"
            ? row.draft_status === DRAFT_STATUS.EMPTY ||
              row.draft_status === DRAFT_STATUS.SUBMITTED ||
              row.draft_status === DRAFT_STATUS.SUBMITTED_REDRAW
            : row.draft_status === DRAFT_STATUS.DRAFT,
        )
        .sort((valueA, valueB) =>
          defaultSort(
            new Date(valueB.created_at || "").getTime(),
            new Date(valueA.created_at || "").getTime(),
          ),
        ),
    [allOrderedRequisitions, searchText, activeSubTab],
  );

  const redrawDrafts = useMemo(
    () =>
      allOrderedRequisitions.filter(
        (row) => row.draft_status === DRAFT_STATUS.REDRAW_DRAFT,
      ),
    [allOrderedRequisitions],
  );

  const visibleRedrawDrafts = useMemo(
    () => redrawDrafts.filter((row) => !row.is_redraw_alert_dismissed),
    [redrawDrafts],
  );

  const {
    cfcEligiblePatients,
    cfcModalOpen,
    setCfcModalOpen,
    cfcModalInitialTab,
    setCfcModalInitialTab,
    cfcOrderModalData,
    setCfcOrderModalData,
    handleCfcOrderFromAlert,
    handleCfcMuteFromAlert,
    handleCfcOrderSubmitted,
    getGlobalSearchCfcOrderData,
    handleGlobalSearchCfcOrder,
  } = useCfcAlerts({
    canUseProviderCfc: canAccessProviderCfc,
    completedRows,
    processingRows,
    token: auth?.token,
    username: auth?.username,
    setLoggedOut,
    toast,
    refreshVisibleRequisitionData: refresh.refreshVisibleRequisitionData,
    refreshAfterCfcOrderSubmitted: refresh.refreshAfterCfcOrderSubmitted,
    setRequisitions,
    setSearchRequisitions,
    setAttestationModalOpen,
  });

  const handleRedrawViewOrders = async () => {
    if (!auth) return;
    await RefreshNewRequisitionOrders(
      auth.token,
      setAllOrderedRequisitions,
      setLoggedOut,
      setOrderedRequestComplete,
    );
    setRedrawModalOpen(false);
    setActiveTab(getTabIndex("ORDERED", tabs));
    setActiveSubTab("ORDERED");
    setSearchText("");
  };

  const handleRedrawRowClick = async (patientName: string) => {
    if (!auth) return;
    await RefreshNewRequisitionOrders(
      auth.token,
      setAllOrderedRequisitions,
      setLoggedOut,
      setOrderedRequestComplete,
    );
    setRedrawModalOpen(false);
    setActiveTab(getTabIndex("ORDERED", tabs));
    setActiveSubTab("ORDERED");
    setSearchText(patientName);
  };

  const handleEditRedrawDraft = (order: NewRequisitionType) => {
    TrackAnalyticsEvent("RedrawEditClicked", auth?.token, auth?.username, {
      source: "redraw_modal",
    });
    const convertedFormData = convertNewReqTypeToForm(order);
    setGlobalNewRequisition(convertedFormData);
    setDraftEditReq(order);
    setEditingRedrawOrderId(order.unity_complete_id);
    setRedrawModalOpen(false);
    originatingTabRef.current = activeTab;
    setNewReqOpen(true);
  };

  const handleSubmittedOrderEdit = (order: NewRequisitionType) => {
    TrackAnalyticsEvent(
      "ProviderElectronicOrderEditClicked",
      auth?.token,
      auth?.username,
      {
        source: "provider_grid",
      },
    );
    const convertedFormData = convertNewReqTypeToForm(order);
    setGlobalNewRequisition(convertedFormData);
    setEditingOrder(order);
    setRedrawModalOpen(false);
  };

  const handleEditOrderSubmitted = async () => {
    if (!auth) return;
    await RefreshNewRequisitionOrders(
      auth.token,
      setAllOrderedRequisitions,
      setLoggedOut,
      setOrderedRequestComplete,
    );
    setEditingOrder(undefined);
    toast.showToast("Order edited successfully", { autoHideDuration: 2000 });
  };

  const handleRequestReportUpdate = async (row: RequisitionRow) => {
    if (!auth) return;
    TrackAnalyticsEvent(
      "ProviderReportUpdateClicked",
      auth.token,
      auth.username,
      { source: "provider_grid" },
    );
    lastReportUpdateClickRef.current = row.sfID;
    const response = await GetSpecificRequisition(
      auth.token,
      row.sfID,
      setLoggedOut,
    );
    const requisition: DetailedRequisitionSerializer | undefined = response?.ok
      ? await response.json()
      : undefined;
    // Open only the order clicked last, even if an earlier one loads later.
    if (lastReportUpdateClickRef.current !== row.sfID) return;
    if (!requisition) {
      toast.showError("Failed to load the order");
      return;
    }
    setReportUpdateRequisition(requisition);
  };

  const handleReportUpdateSubmitted = async () => {
    await refresh.refreshVisibleRequisitionData();
    setReportUpdateRequisition(undefined);
    toast.showToast("Update requested successfully", {
      autoHideDuration: 2000,
    });
  };

  const { handleRedrawDismissFromAlert } = useRedrawAlertDismissal({
    token: auth?.token,
    setLoggedOut,
    setAllOrderedRequisitions,
    setOrderedRequestComplete,
    toast,
  });

  // Clear redraw alert editing state so dropdown selections return to the main view after submission.
  const handleSelectRedrawDraft = (order: NewRequisitionType) => {
    TrackAnalyticsEvent("RedrawEditClicked", auth?.token, auth?.username, {
      source: "ordering_shortcuts",
    });
    setEditingRedrawOrderId(null);
    setNewReqOpen(false);
    const convertedFormData = convertNewReqTypeToForm(order);
    setGlobalNewRequisition(convertedFormData);
    setDraftEditReq(order);
    setNewReqOpen(true);
  };

  const filteredArchivedRows = useMemo(
    () =>
      archivedRequisitions
        .filter(
          (row) =>
            (row.name?.toLowerCase() || "").includes(
              searchText.toLowerCase(),
            ) ||
            (row.physician?.toLowerCase() || "").includes(
              searchText.toLowerCase(),
            ) ||
            (row.reqID?.toLowerCase() || "").includes(
              searchText.toLowerCase(),
            ) ||
            row.medicalRecordNumber
              .toLowerCase()
              .includes(searchText.toLowerCase()) ||
            (row.reported?.toLowerCase() || "").includes(
              searchText.toLowerCase(),
            ),
        )
        .sort((valueA, valueB) =>
          defaultSort(
            new Date(valueB.reported || "").getTime(),
            new Date(valueA.reported || "").getTime(),
          ),
        ),
    [archivedRequisitions, searchText],
  );

  const getGridRows = (tabType: TabName) => {
    if (tabType === "ORDERED" && !orderedRequestComplete) return undefined;
    if (tabType === "ARCHIVED" && !archivedRequestComplete) return undefined;
    if (
      (tabType === "COMPLETED" || tabType === "PROCESSING") &&
      !defaultRequestComplete
    )
      return undefined;
    if (tabType === "COMPLETED") return filteredCompletedRows;
    if (tabType === "PROCESSING") return filteredProcessingRows;
    if (tabType === "ORDERED") return filteredOrderedRows;
    if (tabType === "ARCHIVED") return filteredArchivedRows;
    return [];
  };

  const getOppositeGridInfo = (tabType: TabName) => {
    if (tabType === "COMPLETED")
      return (
        Boolean(filteredProcessingRows.length) ||
        Boolean(filteredOrderedRows.length)
      );
    if (tabType === "PROCESSING")
      return (
        Boolean(filteredCompletedRows.length) ||
        Boolean(filteredOrderedRows.length)
      );
    if (tabType === "ORDERED")
      return (
        Boolean(filteredProcessingRows.length) ||
        Boolean(filteredCompletedRows.length)
      );
    if (tabType === "ARCHIVED")
      return (
        Boolean(filteredProcessingRows.length) ||
        Boolean(filteredCompletedRows.length)
      );
    return false;
  };

  const { handleArchive, handleUnarchive } = useArchiveActions({
    token: auth?.token,
    username: auth?.username,
    setLoggedOut,
    toast,
    refreshVisibleRequisitionData: refresh.refreshVisibleRequisitionData,
    completedRows: filteredCompletedRows,
    archivedRows: filteredArchivedRows,
  });

  const globalSearchButton = (
    <Tooltip title="Referred Patient Search" arrow placement="top">
      <GlobalSearchIconButton
        type="button"
        onClick={() => {
          TrackAnalyticsEvent(
            "global_search_open",
            auth?.token,
            auth?.username,
          );
          setAttestationModalOpen(true);
        }}
      >
        <FontAwesomeIcon icon={faUserMagnifyingGlass} />
      </GlobalSearchIconButton>
    </Tooltip>
  );

  return (
    <>
      <CustomGridWrapper data-testid="CustomGridWrapper">
        {shouldShowBetaBanner && (
          <Banner
            bannerType="information"
            bannerTitle={betaFeature!.title}
            bannerText={betaFeature!.description}
            bannerActionCTA="No, thank you"
            bannerActionOnClick={() => {
              handleBetaFeatureResponse(
                betaFeature!.id,
                betaFeature!.name,
                false,
                auth!,
                setLoggedOut,
                setShouldShowBetaBanner,
              );
            }}
            bannerActionSecondaryCTA="Yes, please"
            bannerActionSecondaryOnClick={() => {
              handleBetaFeatureResponse(
                betaFeature!.id,
                betaFeature!.name,
                true,
                auth!,
                setLoggedOut,
                setShouldShowBetaBanner,
              );
            }}
          />
        )}
        {providerAdvocacyEnabled && shouldShowProviderLetterBanner && (
          <Banner
            bannerType="warning"
            bannerTitle="Sign the petition to protect provider choice and blood draw access to UNITY Complete®"
            bannerText="Protect patient access and provider choice."
            bannerActionCTA="Learn More"
            bannerActionOnClick={() => setProviderLetterOpen(true)}
            bannerActionSecondaryCTA="Dismiss"
            bannerActionSecondaryOnClick={() => {
              localStorage.setItem(providerAdvocacyDismissedStorageKey, "true");
              setShouldShowProviderLetterBanner(false);
            }}
          />
        )}
        <ProviderLetterModal
          generateNotesModalOpen={providerLetterOpen}
          setGenerateNotesModalOpen={setProviderLetterOpen}
          setCheck={setShouldShowProviderLetterBanner}
        />
        {!canAccessProviderCfc && (
          <>
            <MissingInfoModal
              setMissingInfoModalOpen={setMissingInfoModalOpen}
              missingInfoModalOpen={missingInfoModalOpen}
            />
            <RedrawModal
              open={redrawModalOpen}
              onClose={async () => {
                setRedrawModalOpen(false);
                setEditingRedrawOrderId(null);
                if (auth) {
                  await RefreshNewRequisitionOrders(
                    auth.token,
                    setAllOrderedRequisitions,
                    setLoggedOut,
                    setOrderedRequestComplete,
                  );
                }
              }}
              redrawDrafts={redrawDrafts}
              onViewOrders={handleRedrawViewOrders}
              onRowClick={handleRedrawRowClick}
              onEditOrder={handleEditRedrawDraft}
            />
          </>
        )}
        <PatientReleaseModal
          setPatientReleaseModalOpen={setPatientReleaseModalOpen}
          patientReleaseModalOpen={patientReleaseModalOpen}
        />
        <AddOnModal
          setAddOnModalData={setAddOnModalData}
          addOnModalData={addOnModalData}
        />
        {canAccessProviderCfc && (
          <CfcEligibilityModal
            open={cfcModalOpen}
            onClose={() => setCfcModalOpen(false)}
            eligiblePatients={cfcEligiblePatients}
            onOrderCfc={handleCfcOrderFromAlert}
            onMuteCfc={handleCfcMuteFromAlert}
            initialTab={cfcModalInitialTab}
            redrawDrafts={visibleRedrawDrafts}
            onRedrawViewOrders={() => {
              setCfcModalOpen(false);
              handleRedrawViewOrders();
            }}
            onRedrawRowClick={(patientName) => {
              setCfcModalOpen(false);
              handleRedrawRowClick(patientName);
            }}
            onRedrawEditOrder={(order) => {
              setCfcModalOpen(false);
              handleEditRedrawDraft(order);
            }}
            onRedrawDismiss={handleRedrawDismissFromAlert}
          />
        )}
        {canAccessProviderCfc && cfcOrderModalData && (
          <CfcOrderModal
            open={Boolean(cfcOrderModalData)}
            onClose={() => setCfcOrderModalData(null)}
            patientData={cfcOrderModalData}
            onOrderSubmitted={handleCfcOrderSubmitted}
            canSubmitOrder={canCreateElectronicOrders}
          />
        )}
        <HeaderLayout
          deviceType={deviceType}
          globalSearchEnabled={globalSearchEnabled}
        >
          <HeaderSearchArea>
            <SearchInputBox>
              <TextField
                variant="standard"
                inputRef={searchRef}
                fullWidth
                placeholder="SEARCH"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchText ? (
                    <InputAdornment position="end">
                      <ClearSearchIcon
                        onClick={() => {
                          setSearchText("");
                          searchRef.current?.focus();
                        }}
                      />
                    </InputAdornment>
                  ) : undefined,
                  disableUnderline: true,
                }}
              />
            </SearchInputBox>
          </HeaderSearchArea>
          {globalSearchEnabled && deviceType === "desktop" && (
            <GlobalSearchButtonArea>
              {globalSearchButton}
            </GlobalSearchButtonArea>
          )}
          <HeaderTabsArea>
            <HeaderTabsContainer>
              <HeaderTabsScroller>
                {tabs.map((tabName, index) => (
                  <CustomGridTab
                    key={tabName}
                    selected={activeTab === index}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab(index);
                      localStorage?.setItem("btoProviderCurrentTab", tabName);
                    }}
                  >
                    {tabName}
                  </CustomGridTab>
                ))}
              </HeaderTabsScroller>
            </HeaderTabsContainer>
          </HeaderTabsArea>
          <HeaderActionsArea>
            {/* On desktop, the search button has its own grid position between
              the search bar and tabs. On mobile it joins the actions row. */}
            {globalSearchEnabled &&
              deviceType !== "desktop" &&
              globalSearchButton}
            {auth?.is_electronic_ordering_enabled &&
              activeTab === getTabIndex("ORDERED", tabs) && (
                <HeaderActionItem>
                  <ProviderHeaderActionButton
                    label="FEDEX PICKUP"
                    startIcon={<LocalShippingIcon />}
                    onClick={async () => {
                      setNewKitPickupOpen(true);
                      await SendActionType(
                        "ProviderKitPickupOpenModal",
                        setLoggedOut,
                        auth?.token,
                      );
                    }}
                    size="small"
                    fullWidth
                  />
                </HeaderActionItem>
              )}
            {canAccessProviderCfc
              ? activeTab !== getTabIndex("ORDERED", tabs) && (
                  <HeaderActionItem>
                    <AlertsButton
                      actionsNeededCount={allMissingRequisitions.length}
                      redrawsCount={visibleRedrawDrafts.length}
                      unityEligibleCount={cfcEligiblePatients.length}
                      actionsNeededLoading={!missingRequestComplete}
                      redrawsLoading={!orderedRequestComplete}
                      unityEligibleLoading={!defaultRequestComplete}
                      onOpenTab={(tab) => {
                        setCfcModalInitialTab(tab);
                        setCfcModalOpen(true);
                      }}
                    />
                  </HeaderActionItem>
                )
              : activeTab !== getTabIndex("ORDERED", tabs) && (
                  <>
                    {Boolean(redrawDrafts.length) && (
                      <HeaderActionItem>
                        <ProviderHeaderRedrawButton
                          startIcon={<FontAwesomeIcon icon={faVials} />}
                          onClick={() => setRedrawModalOpen(true)}
                        >
                          {`REDRAWS (${redrawDrafts.length})`}
                        </ProviderHeaderRedrawButton>
                      </HeaderActionItem>
                    )}
                    {Boolean(allMissingRequisitions.length) && (
                      <HeaderActionNeededItem deviceType={deviceType}>
                        <MissingInfoWarning
                          $missingInfo={Boolean(allMissingRequisitions.length)}
                          onClick={() => setMissingInfoModalOpen(true)}
                        >
                          <FontAwesomeIcon
                            icon={faTriangleExclamation}
                            color="white"
                          />
                          <MissingInfoWarningText
                            $missingInfo={Boolean(
                              allMissingRequisitions.length,
                            )}
                            onClick={() => setMissingInfoModalOpen(true)}
                          >
                            {`ACTION NEEDED (${allMissingRequisitions.length})`}
                          </MissingInfoWarningText>
                        </MissingInfoWarning>
                      </HeaderActionNeededItem>
                    )}
                  </>
                )}
            {canCreateElectronicOrders && (
              <HeaderActionItem>
                <ProviderHeaderActionButton
                  label="ORDER TEST"
                  onClick={async () => {
                    setGlobalNewRequisition(getInitialNewRequisitionForm());
                    setDraftEditReq(undefined);
                    originatingTabRef.current = activeTab;
                    setNewReqOpen(true);
                    await SendActionType(
                      "ProviderClickNewOrder",
                      setLoggedOut,
                      auth?.token,
                    );
                  }}
                  size="small"
                  fullWidth
                />
              </HeaderActionItem>
            )}
          </HeaderActionsArea>
        </HeaderLayout>
        <Box>
          {tabs.map((tabContent, index) => (
            <div key={tabContent}>
              <TabPanel key={tabContent} value={activeTab} index={index}>
                <>
                  {activeTab === getTabIndex("ORDERED", tabs) && (
                    <SubGridHeaderTabs>
                      <SubGridHeaderTabButtonWrapper>
                        {subTabs.map((tabName) => {
                          const reqNumber = allOrderedRequisitions.filter(
                            (row) =>
                              tabName === "ORDERED"
                                ? row.draft_status === DRAFT_STATUS.EMPTY ||
                                  row.draft_status === DRAFT_STATUS.SUBMITTED ||
                                  row.draft_status ===
                                    DRAFT_STATUS.SUBMITTED_REDRAW
                                : row.draft_status === DRAFT_STATUS.DRAFT,
                          ).length;
                          return (
                            <SubCustomGridTab
                              key={tabName}
                              selected={activeSubTab === tabName}
                              onClick={(e) => {
                                e.preventDefault();
                                setActiveSubTab(tabName);
                              }}
                            >
                              {tabName}{" "}
                              <SubTabNumber
                                reqNumber={reqNumber}
                                selected={activeSubTab === tabName}
                              >
                                <SubTabCount>{reqNumber}</SubTabCount>
                              </SubTabNumber>
                            </SubCustomGridTab>
                          );
                        })}
                      </SubGridHeaderTabButtonWrapper>
                    </SubGridHeaderTabs>
                  )}
                  <MyGrid
                    tabType={tabContent}
                    subTabType={activeSubTab}
                    setNewReqOpen={setNewReqOpen}
                    rows={getGridRows(tabContent)}
                    oppositeRows={
                      getOppositeGridInfo(tabContent) ? searchText : ""
                    }
                    setDraftModal={setDraftModal}
                    setPatientReleaseModal={setPatientReleaseModalOpen}
                    setAddOnModal={setAddOnModalData}
                    releaseToPatient={auth?.release_to_patient_enabled || false}
                    onArchive={handleArchive}
                    onUnarchive={handleUnarchive}
                    onPrintError={(message) => toast.showError(message)}
                    onOrderCfc={handleCfcOrderFromAlert}
                    totalReqs={activeTotalReqs}
                    suppressCfcBanners={cfcModalOpen || !canAccessProviderCfc}
                    onEditOrder={handleSubmittedOrderEdit}
                    editOrderEnabled={editOrderEnabled}
                    onRequestReportUpdate={
                      editOrderEnabled ? handleRequestReportUpdate : undefined
                    }
                  />
                </>
              </TabPanel>
            </div>
          ))}
        </Box>
      </CustomGridWrapper>
      <NewRequisition
        handleClose={() => {
          setNewReqOpen(false);
          // Return to the tab user was on when they opened NewRequisition
          setActiveTab(originatingTabRef.current);
          setEditingRedrawOrderId(null);
          if (
            draftEditReq &&
            JSON.stringify(convertNewReqTypeToForm(draftEditReq)) !==
              JSON.stringify(newGlobalRequisition)
          ) {
            setDraftModal("unsaved");
            // Keep the draft identity/status until the user saves or discards.
          } else if (
            !draftEditReq &&
            newGlobalRequisition.firstName &&
            newGlobalRequisition.lastName &&
            newGlobalRequisition.dateOfBirth
          ) {
            setDraftModal("unsaved");
          } else {
            // Return only when cancelling an unchanged redraw draft. Submission
            // clears draftEditReq; unsaved changes need their own dialog first.
            if (editingRedrawOrderId && draftEditReq) {
              if (canAccessProviderCfc) {
                setCfcModalInitialTab(AlertTab.RedrawsRequests);
                setCfcModalOpen(true);
              } else {
                setRedrawModalOpen(true);
              }
            }
            setDraftEditReq(undefined);
            setGlobalNewRequisition(getInitialNewRequisitionForm());
          }
        }}
        setTab={() => setActiveTab(2)}
        newReqOpen={newReqOpen}
        setDraftModal={setDraftModal}
        draftModal={draftModal}
        setNewReqOpen={setNewReqOpen}
        redrawDrafts={redrawDrafts}
        onSelectRedrawDraft={handleSelectRedrawDraft}
      />
      <NewReqEditOrderDialog
        order={editingOrder}
        onClose={() => setEditingOrder(undefined)}
        onSubmitted={handleEditOrderSubmitted}
      />
      <RequestReportUpdateDialog
        requisition={reportUpdateRequisition}
        onClose={() => setReportUpdateRequisition(undefined)}
        onSubmitted={handleReportUpdateSubmitted}
      />
      <NewKitPickup
        handleClose={() => {
          setNewKitPickupOpen(false);
        }}
        newKitPickupOpen={newKitPickupOpen}
      />
      <ActionSnackbar state={toast.state} onClose={toast.closeToast} />
      {auth && attestationModalOpen && (
        <GlobalSearchModal
          onClose={() => setAttestationModalOpen(false)}
          onCheckAttestation={async () => {
            const state = await getAttestationState(auth.token, setLoggedOut);
            return state?.is_valid ?? false;
          }}
          onAttested={async () => {
            await attestGlobalSearch(auth.token, setLoggedOut);
          }}
          onSearch={(firstName, lastName, dob) =>
            searchGlobalPatients(
              auth.token,
              setLoggedOut,
              firstName,
              lastName,
              dob,
            )
          }
          onAffiliate={async (sfid, accountSfid) => {
            const ok = await affiliateRequisition(
              auth.token,
              setLoggedOut,
              sfid,
              accountSfid,
            );
            if (ok) {
              refresh.refreshDefaultAndArchivedRequisitions().catch(() => {});
            }
            return ok;
          }}
          onStartCfcOrder={
            canAccessProviderCfc ? handleGlobalSearchCfcOrder : undefined
          }
          onGetCfcOrderData={
            canAccessProviderCfc ? getGlobalSearchCfcOrderData : undefined
          }
          onFetchAccounts={() => getProviderAccounts(auth.token, setLoggedOut)}
          auth={auth}
        />
      )}
    </>
  );
};

export default CustomGrid;
